/**
 * Zod validation schemas for form validation with react-hook-form
 * These schemas replace the custom validation utilities with Zod's powerful validation
 */

import { ZodIssueCode, z } from 'zod';
import { sanitizeText, sanitizeUrl } from './sanitization';

// Safe content validation is now integrated directly into stringWithLength and requiredString functions

/**
 * Email validation schema
 */
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

/**
 * URL validation schema with safety check
 */
export const urlSchema = z
  .string()
  .url('Please enter a valid URL')
  .superRefine((val, ctx) => {
    const sanitized = sanitizeUrl(val);
    if (!sanitized) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: 'Invalid or unsafe URL',
      });
    }

    try {
      const url = new URL(val);
      if (!['http:', 'https:'].includes(url.protocol)) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: 'URL must use HTTP or HTTPS protocol',
        });
      }
    } catch {
      // Already handled by .url() check
    }
  });

/**
 * Create a string schema with min/max length and safe content validation
 */
export const stringWithLength = (min: number, max: number, fieldName?: string) =>
  z.string()
    .min(min, `${fieldName || 'Field'} must be at least ${min} characters`)
    .max(max, `${fieldName || 'Field'} must be less than ${max} characters`)
    .refine(
      (val) => {
        const sanitized = sanitizeText(val);
        return sanitized === val;
      },
      {
        message: 'Input contains invalid characters',
      }
    );

/**
 * Create a required string schema with safe content validation
 */
export const requiredString = (fieldName: string) =>
  z.string()
    .min(1, `${fieldName} is required`)
    .refine(
      (val) => {
        const sanitized = sanitizeText(val);
        return sanitized === val;
      },
      {
        message: 'Input contains invalid characters',
      }
    );

/**
 * Array length validation
 */
export const arrayWithLength = <T extends z.ZodTypeAny>(
  itemSchema: T,
  min: number,
  max: number
) =>
  z
    .array(itemSchema)
    .min(min, `At least ${min} item${min === 1 ? '' : 's'} required`)
    .max(max, `Maximum ${max} item${max === 1 ? '' : 's'} allowed`);

/**
 * Tags validation (comma-separated)
 */
export const tagsSchema = (min: number, max: number) =>
  z
    .string()
    .transform((val) =>
      val
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
    )
    .pipe(
      z
        .array(z.string().refine(
          (val) => {
            const sanitized = sanitizeText(val);
            return sanitized === val;
          },
          {
            message: 'Input contains invalid characters',
          }
        ))
        .min(min, `At least ${min} tag${min === 1 ? '' : 's'} required`)
        .max(max, `Maximum ${max} tag${max === 1 ? '' : 's'} allowed`)
    );

/**
 * Multiline items validation
 */
export const multilineItemsSchema = (min: number, max: number) =>
  z
    .string()
    .transform((val) =>
      val
        .split('\n')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    )
    .pipe(
      z
        .array(z.string())
        .min(min, `At least ${min} item${min === 1 ? '' : 's'} required`)
        .max(max, `Maximum ${max} item${max === 1 ? '' : 's'} allowed`)
    );

/**
 * Enum validation helper
 */
export const createEnumSchema = <T extends readonly [string, ...string[]]>(
  values: T,
  message?: string
) => z.enum(values, {
  errorMap: () => ({ message: message || `Must be one of: ${values.join(', ')}` }),
});

/**
 * Optional URL schema (empty string allowed)
 */
export const optionalUrlSchema = z.union([
  z.literal(''),
  urlSchema,
]);

/**
 * Common form schemas for reuse
 */
export const commonSchemas = {
  get name() { return stringWithLength(3, 50, 'Name'); },
  get description() { return stringWithLength(10, 500, 'Description'); },
  get shortDescription() { return stringWithLength(10, 150, 'Short description'); },
  get email() { return emailSchema; },
  get url() { return urlSchema; },
  get optionalUrl() { return optionalUrlSchema; },
  get tags() { return tagsSchema(1, 10); },
  get capabilities() { return multilineItemsSchema(1, 20); },
  get features() { return multilineItemsSchema(1, 20); },
} as const;

/**
 * Helper to create form validation resolver
 * Usage: resolver: zodResolver(schema)
 */
export { zodResolver } from '@hookform/resolvers/zod';

/**
 * Re-export z for convenience
 */
export { z };
